import React, { useState } from 'react'
import { useSelector } from 'react-redux'
import { Helmet } from 'react-helmet'
import { Button, Checkbox, Heading, HStack, Spinner, Stack } from '@chakra-ui/react'
import { ArrowDownTrayIcon, PlusIcon } from '@heroicons/react/20/solid'
import { GridApi, GridReadyEvent } from 'ag-grid-community'
import { CAPABILITY_TYPES } from '@homevest/utils/capabilities'

import { useEvictionsQuery } from 'graphql/generated'
import { hasCapability } from 'lib/admin-perms'
import { StoreState } from 'store'
import EvictionsGrid, { Eviction } from './Grid'
import CreateEvictionModal from './CreateEvictionModal'
import EvictionSidepeek from './EvictionSidepeek'

const EvictionsScreen = () => {
  const [showCompleted, setShowCompleted] = useState(false)
  const [showConsentJudgementsOnly, setShowConsentJudgementsOnly] = useState(false)
  const [gridApi, setGridApi] = useState<GridApi>()
  const [activeEviction, setActiveEviction] = useState<Eviction | null>(null)
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false)
  const admin = useSelector<StoreState, any>((state) => state.admin)
  const canManageEvictions = hasCapability(admin, CAPABILITY_TYPES.EVICTION_MANAGER)

  const [{ data, fetching }, refetch] = useEvictionsQuery({
    requestPolicy: 'network-only',
    context: React.useMemo(() => ({ suspense: false }), [])
  })

  const evictions = data?.evictions ?? []

  const filteredEvictions = evictions
    .filter((e) => showCompleted || !['canceled', 'completed'].includes(e.status ?? ''))
    .filter((e) => !showConsentJudgementsOnly || e.consent_judgement === true)

  const onGridReady = (evt: GridReadyEvent<Eviction>) => {
    setGridApi(evt.api)
  }

  const handleCsvExport = () => {
    const currentTimestamp = new Date().getTime()
    gridApi?.exportDataAsCsv({
      allColumns: true,
      fileName: `evictions_export_${currentTimestamp}.csv`
    })
  }

  const handleRefetch = () => {
    refetch({ requestPolicy: 'network-only' })
  }

  return (
    <>
      <Helmet>
        <title>Evictions Manager</title>
      </Helmet>
      <Stack padding={4} spacing={4}>
        <HStack justifyContent='space-between'>
          <Heading>Evictions Manager</Heading>
          <HStack>
            <Button
              size='xs'
              onClick={() => setIsCreateModalOpen(true)}
              leftIcon={<PlusIcon className='h-4 w-4' />}
              colorScheme='teal'
              isDisabled={!canManageEvictions}
            >
              Create Eviction
            </Button>
            <Button
              size='xs'
              onClick={handleCsvExport}
              leftIcon={<ArrowDownTrayIcon className='h-4 w-4' />}
              colorScheme='teal'
              variant='outline'
            >
              Export to CSV
            </Button>
          </HStack>
        </HStack>

        <HStack spacing={6}>
          <Checkbox isChecked={showCompleted} onChange={(e) => setShowCompleted(e.target.checked)}>
            Show completed &amp; canceled evictions
          </Checkbox>
          <Checkbox
            isChecked={showConsentJudgementsOnly}
            onChange={(e) => setShowConsentJudgementsOnly(e.target.checked)}
          >
            Show consent judgements only
          </Checkbox>
        </HStack>

        {fetching && !data ? (
          <Spinner />
        ) : (
          <EvictionsGrid
            data={filteredEvictions}
            onGridReady={onGridReady}
            setActiveEviction={setActiveEviction}
          />
        )}
      </Stack>

      <CreateEvictionModal
        isOpen={isCreateModalOpen}
        isDisabled={!canManageEvictions}
        onClose={() => {
          setIsCreateModalOpen(false)
          handleRefetch()
        }}
      />

      <EvictionSidepeek
        eviction={activeEviction}
        isOpen={activeEviction !== null}
        onClose={() => {
          setActiveEviction(null)
          handleRefetch()
        }}
      />
    </>
  )
}

export default EvictionsScreen
